import {authService} from '@services/auth.service';
import type {PortalUser} from '../types';
import {
    getEmpresaIdFromJwt,
    getEmpresaIdFromUser,
    getPortalPermissions,
    hasCompletePortalContext,
    isImpersonatedSession,
    isImpersonationPortalReady
} from './jwt';

export const isAdminEmpresaCliente = (user: PortalUser | null | undefined): boolean =>
    authService.isAdminEmpresaCliente(user as Record<string, unknown> | null);

export const hasClientPortalPermission = (
    user: PortalUser | null | undefined,
    token?: string | null
): boolean => {
    const permissions = getPortalPermissions(user, token);
    return permissions.some(permission => permission.startsWith('client_portal:'));
};

export const canAccessClientPortal = (
    user: PortalUser | null | undefined,
    token?: string | null
): boolean =>
    authService.isImpersonationViewActive(token ?? undefined) ||
    isImpersonationPortalReady(token ?? null) ||
    (isImpersonatedSession(token ?? null) && Boolean(getEmpresaIdFromJwt(token ?? null))) ||
    (authService.hasImpersonationBackup() && Boolean(getEmpresaIdFromJwt(token ?? null))) ||
    isAdminEmpresaCliente(user) ||
    hasClientPortalPermission(user, token);

export const requiresEmpresaSelection = (
    user: PortalUser | null | undefined,
    token?: string | null
): boolean => {
    if (
        authService.isImpersonationViewActive(token ?? undefined) ||
        isImpersonationPortalReady(token ?? null)
    ) {
        return false;
    }

    if (!isAdminEmpresaCliente(user)) {
        return false;
    }

    return !hasCompletePortalContext(token ?? null);
};

export const resolveClientPortalHomePath = (
    user: PortalUser | null | undefined,
    token?: string | null
): string => {
    if (!canAccessClientPortal(user, token)) {
        return '/login';
    }

    if (requiresEmpresaSelection(user, token)) {
        return authService.needsPortalEmpresaPicker(user as Record<string, unknown> | null, token)
            ? '/login?portal_step=empresa'
            : '/login?reason=empresa-context';
    }

    return '/portal-cliente/dashboard';
};
